import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Target, Globe, Banknote, LogOut, Sparkles, ChevronRight, Trophy, Loader2, Clock, Calendar, Rocket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import DailyCoaching from '../components/DailyCoaching';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  heart: Heart,
  target: Target,
  globe: Globe,
  banknote: Banknote
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, fetchUser } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchModules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/modules?language=${language}`);
      setModules(response.data);
    } catch (e) {
      console.warn('Modules fetch:', e.message);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const handleGenerateAnalysis = async () => {
    // Check if user has paid
    try {
      const statusResponse = await axios.get(`${API}/payment/check`);
      if (!statusResponse.data.has_diagnostic) {
        // Redirect to payment page
        navigate('/payment');
        return;
      }
    } catch (e) {
      console.warn('Payment check:', e.message);
    }

    setGenerating(true);
    try {
      // AI analysis can take 30-90 seconds
      const response = await axios.post(`${API}/analysis/generate`, {}, { timeout: 120000 });
      toast.success(language === 'fr' ? 'Profil Ikigai généré !' : 'Ikigai profile generated!');
      navigate('/results');
    } catch (error) {
      if (error.response?.status === 402) {
        // Payment required
        navigate('/payment');
      } else if (error.code === 'ECONNABORTED') {
        toast.error(language === 'fr' ? 'L\'analyse prend plus de temps. Réessayez dans un instant.' : 'Analysis is taking longer. Please retry in a moment.');
      } else {
        toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la génération' : 'Generation failed'));
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const completedModules = modules.filter(m => m.completed).length;
  const canGenerateProfile = completedModules >= 1;

  const progressMessages = {
    0: {
      fr: 'Chaque grande transformation commence par un premier pas. Lance-toi.',
      en: 'Every great transformation starts with a single step. Go for it.'
    },
    1: {
      fr: 'Tu as franchi le premier pas ! Ton Ikigai commence à se dessiner...',
      en: 'You\'ve taken the first step! Your Ikigai is starting to take shape...'
    },
    2: {
      fr: 'À mi-chemin ! L\'IA a déjà de quoi travailler, mais plus tu réponds, plus ton profil sera précis.',
      en: 'Halfway there! The AI already has material to work with, but the more you answer, the sharper your profile.'
    },
    3: {
      fr: 'Plus qu\'un module ! Tu es sur le point de découvrir ce que peu de gens prennent le temps de chercher.',
      en: 'One module left! You\'re about to discover what few people take the time to seek.'
    },
    4: {
      fr: 'Tu as tout donné. Ton analyse personnalisée t\'attend — elle pourrait changer la trajectoire de ta vie.',
      en: 'You gave it your all. Your personalized analysis awaits — it could change the course of your life.'
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="flex items-center gap-3 text-stone-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>{t('loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FFE66D] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-xl font-semibold text-stone-800">Ikigai</span>
          </div>
          
          <div className="flex items-center gap-4">
            {user?.email === 'monikigaiia@gmail.com' && (
              <>
                <button
                  onClick={() => navigate('/admin/users')}
                  className="text-xs text-stone-400 hover:text-[#4ECDC4] transition-colors"
                  data-testid="admin-users-link"
                >
                  Suivi
                </button>
                <button
                  onClick={() => navigate('/admin/promo')}
                  className="text-xs text-stone-400 hover:text-[#FF6B6B] transition-colors"
                  data-testid="admin-promo-link"
                >
                  Codes promo
                </button>
              </>
            )}
            <button
              onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
              className="text-sm text-stone-600 hover:text-stone-900 transition-colors"
              data-testid="dashboard-language-toggle"
            >
              {language === 'fr' ? 'EN' : 'FR'}
            </button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-stone-600 hover:text-stone-900 flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              {t('logout')}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="font-serif text-4xl md:text-5xl font-semibold text-stone-900 mb-2">
                {t('welcome')}, {user?.name} 👋
              </h1>
              <p className="text-lg text-stone-600">{t('yourJourney')}</p>
            </div>
            
            {/* Level Badge */}
            <div className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-soft border border-stone-100">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FFE66D] to-[#FF6B6B] flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-stone-500">{t('level')}</p>
                <p className="font-serif text-lg font-semibold text-stone-900">{user?.level}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Section */}
        <div className="card-ikigai mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="font-serif text-2xl font-semibold text-stone-900 mb-1">{t('progress')}</h2>
              <p className="text-stone-600">
                {completedModules}/4 {t('modulesCompleted')}
              </p>
            </div>
            <div className="text-right">
              <span className="font-serif text-4xl font-bold text-[#FF6B6B]">{user?.total_progress || 0}%</span>
            </div>
          </div>
          <Progress value={user?.total_progress || 0} className="h-4 bg-stone-100" />
          
          {/* Progress indicators */}
          <div className="flex justify-between mt-4">
            {modules.map((module, index) => (
              <div key={module.id} className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    module.completed 
                      ? 'bg-gradient-to-br from-[#FF6B6B] to-[#FFE66D]' 
                      : 'bg-stone-200'
                  }`}
                >
                  {module.completed && (
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="text-xs text-stone-500 mt-2 hidden sm:block">{module.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Quiz CTA - Show if user hasn't done mini-profile yet */}
        {!user?.has_mini_profile && !user?.purchases?.includes('diagnostic') && (
          <div className="mb-8 bg-gradient-to-r from-[#FF6B6B] to-[#FFE66D] rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h2 className="font-serif text-xl font-bold mb-1">
                  {language === 'fr' ? 'Découvre ton Ikigai en 2 minutes' : 'Discover your Ikigai in 2 minutes'}
                </h2>
                <p className="text-white/80 text-sm">
                  {language === 'fr' 
                    ? '8 questions. 1 aperçu gratuit de ton profil par l\'IA. Zéro engagement.'
                    : '8 questions. 1 free AI preview of your profile. Zero commitment.'}
                </p>
              </div>
              <Button
                onClick={() => navigate('/quick-quiz')}
                className="bg-white text-[#FF6B6B] hover:bg-white/90 rounded-xl font-semibold px-6 whitespace-nowrap shadow-md"
                data-testid="start-quick-quiz-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Commencer' : 'Start now'}
              </Button>
            </div>
          </div>
        )}

        {/* Mini-profile teaser - Show if user has mini profile but hasn't paid */}
        {user?.has_mini_profile && !user?.purchases?.includes('diagnostic') && (
          <div className="mb-8 bg-white rounded-2xl p-5 border border-[#4ECDC4]/30 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#4ECDC4]/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#4ECDC4]" />
                </div>
                <div>
                  <p className="font-medium text-stone-900 text-sm">
                    {language === 'fr' ? 'Ton aperçu gratuit est prêt' : 'Your free preview is ready'}
                  </p>
                  <p className="text-xs text-stone-400">
                    {language === 'fr' ? 'Consulte-le ou débloque le profil complet' : 'View it or unlock the full profile'}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/mini-results')}
                variant="outline"
                className="rounded-xl text-sm border-[#4ECDC4] text-[#4ECDC4] hover:bg-[#4ECDC4]/5"
                data-testid="view-mini-results-btn"
              >
                {language === 'fr' ? 'Voir' : 'View'}
              </Button>
            </div>
          </div>
        )}

        {/* 30 Days Explanation */}
        <div className="bg-gradient-to-r from-[#FF6B6B]/5 via-[#FFE66D]/5 to-[#4ECDC4]/5 rounded-2xl p-6 mb-8 border border-stone-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-white shadow-soft flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-[#FF6B6B]" />
            </div>
            <div>
              <p className="text-stone-700 text-sm font-medium leading-relaxed">
                {progressMessages[completedModules]?.[language] || progressMessages[0]?.[language]}
              </p>
              {completedModules < 4 && (
                <p className="text-stone-400 text-xs mt-2">
                  {language === 'fr' 
                    ? '1 à 2 questions par jour — laisse mûrir tes réponses.'
                    : '1-2 questions per day — let your answers mature.'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Daily Coaching - Only show if user has diagnostic */}
        <div className="mb-8">
          <DailyCoaching 
            language={language} 
            hasDiagnostic={user?.purchases?.includes('diagnostic')}
          />
        </div>

        {/* Modules Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {modules.map((module, index) => {
            const Icon = iconMap[module.icon] || Heart;
            const gradientClass = ['gradient-passion', 'gradient-talent', 'gradient-utility', 'gradient-monetization'][index];
            
            return (
              <div
                key={module.id}
                className={`module-card ${gradientClass} group cursor-pointer`}
                onClick={() => navigate(`/module/${module.id}`)}
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`module-card-${module.id}`}
              >
                <div className="relative z-10">
                  {/* Image */}
                  <div className="h-40 rounded-2xl overflow-hidden mb-4">
                    <img
                      src={module.image}
                      alt={module.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-5 h-5 text-white" />
                        <h3 className="font-serif text-xl font-semibold text-white">{module.name}</h3>
                      </div>
                      <p className="text-white/80 text-sm">{module.description}</p>
                      <div className="flex flex-wrap items-center gap-3 mt-3">
                        <span className="bg-white/20 backdrop-blur text-white text-xs px-2 py-1 rounded-full">
                          {module.week_label}
                        </span>
                        <div className="flex items-center gap-1 text-white/70 text-xs">
                          <Clock className="w-3 h-3" />
                          <span>{module.active_time}</span>
                        </div>
                        <div className="flex items-center gap-1 text-white/70 text-xs">
                          <Calendar className="w-3 h-3" />
                          <span>{module.rhythm}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {module.completed ? (
                        <span className="bg-white/20 backdrop-blur text-white text-xs px-3 py-1 rounded-full">
                          {t('completed')} ✓
                        </span>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center group-hover:bg-white/30 transition-colors">
                          <ChevronRight className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Generate Profile CTA */}
        {!user?.purchases?.includes('diagnostic') ? (
          <div className="card-ikigai p-8 bg-gradient-to-br from-[#FF6B6B]/5 to-[#4ECDC4]/5 text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-4 text-[#FF6B6B]" />
            <h2 className="font-serif text-2xl font-semibold text-stone-900 mb-2">
              {canGenerateProfile
                ? (language === 'fr' ? 'Ton Ikigai est prêt à être révélé' : 'Your Ikigai is ready to be revealed')
                : (language === 'fr' ? 'Ton Ikigai t\'attend' : 'Your Ikigai awaits')}
            </h2>
            <p className="text-stone-600 mb-3 max-w-md mx-auto">
              {canGenerateProfile 
                ? (language === 'fr' 
                    ? 'L\'IA va analyser tes réponses pour créer ton profil unique : personnalité, carrières idéales, projets concrets et coaching personnalisé.'
                    : 'AI will analyze your answers to create your unique profile: personality, ideal careers, concrete projects and personalized coaching.')
                : (language === 'fr'
                    ? 'Complète au moins un module pour que l\'IA puisse analyser tes réponses.'
                    : 'Complete at least one module so the AI can analyze your answers.')}
            </p>
            {canGenerateProfile && (
              <div className="mb-5">
                <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-stone-100">
                  <span className="font-serif text-2xl font-bold text-[#FF6B6B]">1,99€</span>
                  <span className="text-stone-400 text-sm">
                    {language === 'fr' ? '— le prix d\'un café pour changer ta vie' : '— the price of a coffee to change your life'}
                  </span>
                </div>
                <p className="text-stone-400 text-xs mt-2">
                  {language === 'fr' ? 'Paiement unique. Accès à vie. Pas d\'abonnement.' : 'One-time payment. Lifetime access. No subscription.'}
                </p>
              </div>
            )}
            <Button
              onClick={handleGenerateAnalysis}
              className="btn-primary flex items-center justify-center gap-2"
              disabled={!canGenerateProfile || generating}
              data-testid="generate-profile-btn"
            >
              {generating ? (
                <div className="flex flex-col items-center gap-1">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {language === 'fr' ? 'Analyse IA en cours...' : 'AI analyzing...'}
                  </div>
                  <span className="text-xs opacity-75">
                    {language === 'fr' ? '(30-60 secondes)' : '(30-60 seconds)'}
                  </span>
                </div>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  {language === 'fr' ? 'Découvrir mon Ikigai' : 'Discover my Ikigai'}
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="card-ikigai p-8 bg-gradient-to-br from-[#FF6B6B]/5 to-[#4ECDC4]/5 text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-4 text-[#FF6B6B]" />
            <h2 className="font-serif text-2xl font-semibold text-stone-900 mb-2">
              {language === 'fr' ? 'Ton profil Ikigai' : 'Your Ikigai profile'}
            </h2>
            <p className="text-stone-600 mb-4 max-w-md mx-auto">
              {language === 'fr' 
                ? 'Consulte ton analyse personnalisée ou régénère-la avec tes nouvelles réponses.'
                : 'View your personalized analysis or regenerate it with your new answers.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/results')}
                className="btn-primary"
                data-testid="view-results-btn"
              >
                {language === 'fr' ? 'Voir mes résultats' : 'View my results'}
              </Button>
              <Button
                onClick={handleGenerateAnalysis}
                className="btn-secondary flex items-center justify-center gap-2"
                disabled={generating}
                data-testid="generate-profile-btn"
              >
                {generating ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {language === 'fr' ? 'Analyse en cours...' : 'Analyzing...'}
                  </div>
                ) : (
                  <>{language === 'fr' ? 'Régénérer' : 'Regenerate'}</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Plan 90 Days CTA - Show if user has purchased diagnostic */}
        {user?.purchases?.includes('diagnostic') && (
          <div className="card-ikigai p-8 mt-8 bg-gradient-to-br from-[#4ECDC4]/10 to-[#1A535C]/10 text-center">
            <Rocket className="w-12 h-12 mx-auto mb-4 text-[#4ECDC4]" />
            <h2 className="font-serif text-2xl font-semibold text-stone-900 mb-2">
              {language === 'fr' ? 'Passe à l\'action !' : 'Take action!'}
            </h2>
            <p className="text-stone-600 mb-4 max-w-md mx-auto">
              {user?.purchases?.includes('plan_90_days')
                ? (language === 'fr' 
                    ? 'Accède à ton plan d\'action personnalisé sur 90 jours.'
                    : 'Access your personalized 90-day action plan.')
                : (language === 'fr'
                    ? 'Transforme ton Ikigai en réalité avec un plan d\'action de 12 semaines.'
                    : 'Turn your Ikigai into reality with a 12-week action plan.')}
            </p>
            {!user?.purchases?.includes('plan_90_days') && (
              <p className="text-sm text-[#4ECDC4] font-medium mb-4">
                {language === 'fr' ? 'Plan 90 jours : 4,99€' : '90-day plan: €4.99'}
              </p>
            )}
            <Button
              onClick={() => user?.purchases?.includes('plan_90_days') ? navigate('/plan') : navigate('/payment')}
              className="bg-[#4ECDC4] hover:bg-[#3DBDB5] text-white rounded-xl h-12 px-8 font-medium"
              data-testid="plan-90-days-btn"
            >
              <Rocket className="w-5 h-5 mr-2" />
              {user?.purchases?.includes('plan_90_days')
                ? (language === 'fr' ? 'Voir mon plan 90 jours' : 'View my 90-day plan')
                : (language === 'fr' ? 'Débloquer mon plan 90 jours' : 'Unlock my 90-day plan')}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
